<template>
  <div class="page">
    <div class="bg-grid" />
    <TopBar />
    <ProjectNav :project="project" />

    <main class="content">
      <!-- Stat cards -->
      <div class="stat-row">
        <StatCard label="TOTAL RUNS" :value="stats.total_runs.toString()" sub="orchestrator cycles" />
        <StatCard
          label="IMPROVEMENTS"
          :value="stats.total_improved.toString()"
          :sub="`${improveRate}% success rate`"
          :trend="stats.total_improved > 0 ? 'positive' : 'neutral'"
        />
        <StatCard label="KNOWLEDGE BASE" :value="stats.rag_count.toLocaleString()" sub="rag documents" />
        <StatCard
          label="ACCURACY"
          :value="stats.last_score > 0 ? stats.last_score.toFixed(1) + '%' : '—'"
          sub="latest eval score"
          :trend="scoreTrend"
        />
      </div>

      <div class="main-grid">
        <!-- Improvement Cycles -->
        <div class="cycles-col">
          <SectionHeader label="Improvement Cycles" :meta="`${orch_runs.length} runs`" />

          <div v-if="orch_runs.length === 0" class="empty-state">
            <p class="empty-title">No orchestrator runs yet</p>
            <p class="empty-body">Trigger the orchestrator from the API or dashboard to start improving this project.</p>
          </div>

          <div v-else class="cycles-list">
            <div v-for="run in orch_runs" :key="run.id" class="cycle-row">
              <div class="cycle-left">
                <span class="cycle-dot" :class="run.was_improvement ? 'green' : (run.status === 'failed' ? 'red' : 'dim')" />
                <div class="cycle-meta">
                  <span class="cycle-trigger">{{ run.triggered_by }}</span>
                  <span class="cycle-date">{{ formatDate(run.created_at) }}</span>
                </div>
              </div>
              <BeforeAfterIndicator
                :before="run.score_before"
                :after="run.score_after"
                :in-progress="run.status !== 'complete' && run.status !== 'failed'"
              />
            </div>
          </div>
        </div>

        <!-- Right column charts -->
        <div class="right-col">
          <!-- 8-Week Accuracy -->
          <div class="chart-panel">
            <div class="panel-head">
              <span class="panel-label">8-WEEK ACCURACY</span>
              <span class="panel-meta">eval scores</span>
            </div>
            <div class="chart-body">
              <div v-if="weekly_evals.length === 0" class="chart-empty">
                No eval runs yet
              </div>
              <BarChart
                v-else
                :data="accuracyChartData"
                color="#4ade80"
              />
            </div>
          </div>

          <!-- Cost Efficiency Trend -->
          <div class="chart-panel">
            <div class="panel-head">
              <span class="panel-label">COST EFFICIENCY</span>
              <span class="panel-meta">weekly spend</span>
            </div>
            <div class="chart-body">
              <div v-if="allZeroCost" class="chart-empty">
                No cost data yet
              </div>
              <BarChart
                v-else
                :data="costChartData"
                color="#f5a623"
              />
            </div>
          </div>

          <!-- Knowledge Growth -->
          <div class="knowledge-panel">
            <div class="panel-head">
              <span class="panel-label">KNOWLEDGE GROWTH</span>
            </div>
            <div class="knowledge-body">
              <div class="knowledge-stat">
                <span class="knowledge-value">{{ stats.rag_count.toLocaleString() }}</span>
                <span class="knowledge-label">total documents</span>
              </div>
              <ProgressBar
                :value="stats.rag_count"
                :max="Math.max(stats.rag_count, 100)"
                color="blue"
              />
              <div class="knowledge-meta">
                <span>Knowledge base grows automatically from requests, corrections, and eval learnings.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed } from "vue"
import TopBar from "../../components/TopBar.vue"
import ProjectNav from "../../components/ProjectNav.vue"
import StatCard from "../../components/StatCard.vue"
import SectionHeader from "../../components/SectionHeader.vue"
import BarChart from "../../components/BarChart.vue"
import ProgressBar from "../../components/ProgressBar.vue"
import BeforeAfterIndicator from "../../components/BeforeAfterIndicator.vue"

const props = defineProps({
  project:      { type: Object, required: true },
  stats:        { type: Object, default: () => ({}) },
  weekly_evals: { type: Array,  default: () => [] },
  weekly_cost:  { type: Array,  default: () => [] },
  orch_runs:    { type: Array,  default: () => [] },
  last_improvement: { type: Object, default: null },
})

const improveRate = computed(() => {
  if (!props.stats.total_runs) return 0
  return ((props.stats.total_improved / props.stats.total_runs) * 100).toFixed(0)
})

const scoreTrend = computed(() => {
  const s = props.stats.last_score
  if (s >= 90) return "positive"
  if (s >= 70) return "neutral"
  if (s > 0)   return "negative"
  return "neutral"
})

const accuracyChartData = computed(() =>
  props.weekly_evals.map((e) => ({
    value:        e.score,
    label:        e.week,
    displayValue: e.score.toFixed(0) + "%",
  }))
)

const costChartData = computed(() =>
  props.weekly_cost.map((e) => ({
    value:        e.cost,
    label:        e.week,
    displayValue: e.cost > 0 ? "$" + e.cost.toFixed(2) : "",
  }))
)

const allZeroCost = computed(() => props.weekly_cost.every((e) => e.cost === 0))

function formatDate(dateStr) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: #0a0a0b;
  position: relative;
}

.bg-grid {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

.content {
  position: relative;
  z-index: 1;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.main-grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 16px;
  align-items: start;
}

.cycles-col,
.right-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-state {
  padding: 40px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.025);
}

.empty-title {
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 700;
  color: #e8e6e3;
  margin: 0 0 6px;
}

.empty-body {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.45);
  margin: 0;
}

.cycles-list {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.cycle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  gap: 16px;
  transition: background 0.15s;
}

.cycle-row:first-child { border-top: none; }
.cycle-row:hover { background: rgba(245, 166, 35, 0.04); }

.cycle-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cycle-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cycle-dot.green  { background: #4ade80; box-shadow: 0 0 6px rgba(74, 222, 128, 0.5); }
.cycle-dot.red    { background: #f87171; box-shadow: 0 0 6px rgba(248, 113, 113, 0.5); }
.cycle-dot.dim    { background: rgba(255, 255, 255, 0.2); }

.cycle-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cycle-trigger {
  font-family: var(--font-mono);
  font-size: 12px;
  color: #e8e6e3;
  text-transform: capitalize;
}

.cycle-date {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}

.chart-panel,
.knowledge-panel {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.panel-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: rgba(232, 230, 227, 0.35);
}

.panel-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}

.chart-body {
  padding: 14px 18px 12px;
}

.chart-empty {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.25);
  text-align: center;
  padding: 24px 0;
}

.knowledge-body {
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.knowledge-stat {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.knowledge-value {
  font-family: var(--font-display);
  font-size: 26px;
  font-weight: 700;
  color: #e8e6e3;
}

.knowledge-label {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.45);
}

.knowledge-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
  line-height: 1.5;
}
</style>
